import global from "../../../common/utility/global";
import { NOMBRES_MES_CORTO } from "./constants";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda mantenga siempre el mismo
// ancho/tamano; sin esto, un establecimiento con nombre muy largo estira
// la leyenda y deforma el layout del grafico (dona corrida, barras
// desalineadas, etc.).
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

// "2026-08-27" -> Date local (evita el corrimiento de dia que da
// "new Date('2026-08-27')" al interpretarse como UTC en un huso horario negativo).
function parsearFechaLocal(fechaStr) {
    const [anio, mes, dia] = fechaStr.slice(0, 10).split("-").map(Number);
    return new Date(anio, mes - 1, dia);
}

function obtenerClaveTiempo(fecha, esMismoMes) {
    return esMismoMes
        ? `${fecha.getDate()} ${NOMBRES_MES_CORTO[fecha.getMonth()]}`
        : `${NOMBRES_MES_CORTO[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

// Arma la serie de "Evolucion" a partir del detalle diario que devuelve el
// servicio: una fila por establecimiento y fecha, ya separada en soles y
// dolares (totalSoles/totalDolares). Se agrupa por dia si el filtro cae en
// un mes, o por mes si abarca mas de uno.
function construirEvolucionDesdeDetalle(detalle, filter, establecimientosReferencia) {
    const esMismoMes = filter.anioInicio === filter.anioFin && filter.mesInicio === filter.mesFin;
    const nombresTop = establecimientosReferencia.slice(0, 4).map((item) => item.nombre);

    const detalleOrdenado = detalle
        .map((fila) => ({ ...fila, __fecha: parsearFechaLocal(fila.fecha) }))
        .sort((a, b) => a.__fecha - b.__fecha);

    const ordenClaves = [];
    const vistos = new Set();
    detalleOrdenado.forEach((fila) => {
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        if (!vistos.has(clave)) {
            vistos.add(clave);
            ordenClaves.push(clave);
        }
    });

    const acumuladoPorEstablecimiento = {};
    nombresTop.forEach((nombre) => {
        acumuladoPorEstablecimiento[nombre] = {};
    });
    detalleOrdenado.forEach((fila) => {
        if (!nombresTop.includes(fila.establecimiento)) return;
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        const previo = acumuladoPorEstablecimiento[fila.establecimiento][clave] || { pen: 0, usd: 0 };
        previo.pen += Number(fila.totalSoles) || 0;
        previo.usd += Number(fila.totalDolares) || 0;
        acumuladoPorEstablecimiento[fila.establecimiento][clave] = previo;
    });

    const construirSerie = (moneda) => nombresTop.map((nombre) => ({
        name: nombre,
        data: ordenClaves.map((clave) => Math.round((acumuladoPorEstablecimiento[nombre][clave] || { pen: 0, usd: 0 })[moneda])),
    }));

    return {
        timeLine: ordenClaves,
        pen: construirSerie("pen"),
        usd: construirSerie("usd"),
    };
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / anterior) * 100;
}

function construirKpiMoneda(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

export function buildDashboardData(response, filter) {
    // El servicio real (comprobanteService.dashboardEstablecimiento) devuelve:
    // - establecimientos: detalle diario (establecimiento, fecha, totalSoles,
    //   totalDolares), una fila por establecimiento y fecha con ventas.
    // - kpis: arreglo de un solo elemento con los totales del periodo y su
    //   comparacion contra el periodo anterior (sufijo "Anterior").
    const detalle = (response && response.establecimientos) || [];
    const kpisApi = (response && response.kpis && response.kpis[0]) || {};


    const acumuladoPorEstablecimiento = {};
    detalle.forEach((fila) => {
        const previo = acumuladoPorEstablecimiento[fila.establecimiento] || { pen: 0, usd: 0 };
        previo.pen += Number(fila.totalSoles) || 0;
        previo.usd += Number(fila.totalDolares) || 0;
        acumuladoPorEstablecimiento[fila.establecimiento] = previo;
    });

    const topEstablecimientos = Object.keys(acumuladoPorEstablecimiento)
        .map((nombre) => ({ nombre, pen: acumuladoPorEstablecimiento[nombre].pen, usd: acumuladoPorEstablecimiento[nombre].usd }))
        .sort((a, b) => b.pen - a.pen);

    const totalVentasPen = topEstablecimientos.reduce((acumulado, item) => acumulado + item.pen, 0);
    const totalVentasUsd = topEstablecimientos.reduce((acumulado, item) => acumulado + item.usd, 0);
    const totalTop3Pen = topEstablecimientos.slice(0, 3).reduce((acumulado, item) => acumulado + item.pen, 0);
    const totalTop3Usd = topEstablecimientos.slice(0, 3).reduce((acumulado, item) => acumulado + item.usd, 0);

    const principales = topEstablecimientos.slice(0, 4);
    const otros = topEstablecimientos.slice(4);
    const totalOtrosPen = otros.reduce((acumulado, item) => acumulado + item.pen, 0);
    const totalOtrosUsd = otros.reduce((acumulado, item) => acumulado + item.usd, 0);
    const participacionBase = otros.length > 0
        ? [...principales, { nombre: `Otros (${otros.length})`, pen: totalOtrosPen, usd: totalOtrosUsd }]
        : principales;

    const construirParticipacion = (moneda) => {
        const totalMoneda = moneda === "pen" ? totalVentasPen : totalVentasUsd;
        return participacionBase.map((item) => ({
            nombre: item.nombre,
            total: item[moneda],
            porcentaje: totalMoneda > 0 ? (item[moneda] / totalMoneda) * 100 : 0,
        }));
    };

    const evolucion = construirEvolucionDesdeDetalle(detalle, filter, topEstablecimientos);

    return {
        kpis: {
            totalVentas: {
                pen: construirKpiMoneda(kpisApi.totalVentasSoles, kpisApi.totalVentasSolesAnterior),
                usd: construirKpiMoneda(kpisApi.totalVentasDolares, kpisApi.totalVentasDolaresAnterior),
            },
            establecimientosActivos: {
                valor: Number(kpisApi.establecimientosActivos) || 0,
                tendenciaValor: (Number(kpisApi.establecimientosActivos) || 0) - (Number(kpisApi.establecimientosActivosAnterior) || 0),
                tendenciaDireccion: direccionTendencia(Number(kpisApi.establecimientosActivos) || 0, Number(kpisApi.establecimientosActivosAnterior) || 0),
            },
            cpePromedio: {
                pen: construirKpiMoneda(kpisApi.cpePromedioSoles, kpisApi.cpePromedioSolesAnterior),
                usd: construirKpiMoneda(kpisApi.cpePromedioDolares, kpisApi.cpePromedioDolaresAnterior),
            },
            ventasPromedioPorEstablecimiento: {
                pen: construirKpiMoneda(kpisApi.ventasPromedioActualSoles, kpisApi.ventasPromedioAnteriorSoles),
                usd: construirKpiMoneda(kpisApi.ventasPromedioActualDolares, kpisApi.ventasPromedioAnteriorDolares),
            },
            // El backend no expone (todavia) el periodo anterior de esta
            // metrica; se calcula solo el valor actual y la tendencia queda
            // plana hasta que el endpoint agregue el dato comparativo.
            concentracionTop3: {
                pen: {
                    valor: totalVentasPen > 0 ? (totalTop3Pen / totalVentasPen) * 100 : 0,
                    tendenciaPct: 0,
                    tendenciaDireccion: "flat",
                },
                usd: {
                    valor: totalVentasUsd > 0 ? (totalTop3Usd / totalVentasUsd) * 100 : 0,
                    tendenciaPct: 0,
                    tendenciaDireccion: "flat",
                },
            },
        },
        topEstablecimientos: topEstablecimientos.slice(0, 10),
        evolucion,
        participacion: {
            pen: construirParticipacion("pen"),
            usd: construirParticipacion("usd"),
        },
    };
}
